import { ItemDrop } from '../entities/ItemDrop.js';
import { DebugLog } from '../core/DebugLog.js';

/**
 * Manages all item drops on the current floor.
 * Handles adding drops, checking pickups, updating animations, and cleanup.
 */
export class ItemManager {
  /**
   * @param {Container} worldContainer - The PixiJS world container to add item sprites to
   * @param {EventBus} eventBus - Event bus for emitting pickup events
   */
  constructor(worldContainer, eventBus) {
    this.worldContainer = worldContainer;
    this.eventBus = eventBus;
    this.items = [];
  }

  /**
   * Add an item drop to the world.
   * @param {number} x - World X position (pixels)
   * @param {number} y - World Y position (pixels)
   * @param {object} itemData - The item data object
   * @param {string} itemId - The item key string
   * @returns {ItemDrop} The created item drop
   */
  addDrop(x, y, itemData, itemId, options = {}) {
    const drop = new ItemDrop(x, y, itemData, itemId, options);
    this.items.push(drop);
    this.worldContainer.addChild(drop.container);
    DebugLog.info('ItemManager', `Dropped ${itemData.name} at (${Math.round(x)}, ${Math.round(y)})`);
    return drop;
  }

  /**
   * Check which items are near the player and return them as picked up.
   * Removes picked-up items from the manager.
   * @param {number} playerX - Player X position (top-left)
   * @param {number} playerY - Player Y position (top-left)
   * @returns {ItemDrop[]} Array of items that were picked up
   */
  checkPickups(playerX, playerY) {
    const pickedUp = [];

    for (let i = this.items.length - 1; i >= 0; i--) {
      const item = this.items[i];
      if (item.isNearPlayer(playerX, playerY)) {
        pickedUp.push(item);
        // Remove from world
        if (item.container.parent) {
          this.worldContainer.removeChild(item.container);
        }
        item.container.destroy();
        this.items.splice(i, 1);
      }
    }

    return pickedUp;
  }

  /**
   * Update all item drop animations.
   * @param {number} dt - Delta time in seconds
   */
  update(dt) {
    for (const item of this.items) {
      item.update(dt);
    }
  }

  /**
   * Remove all items from the world and clear the array.
   */
  clear() {
    for (const item of this.items) {
      if (item.container.parent) {
        this.worldContainer.removeChild(item.container);
      }
      item.container.destroy();
    }
    this.items = [];
  }
}
